import { c as _c } from "react/compiler-runtime";
// The one chip: a rounded pill with a label and ×. Two uses: active filters floating over the board (Chips: frosted
// capsule above the home indicator, plus Clear; each chip removes one filter) and the records a task is about (the
// detail's Context: the label opens the record, × detaches it). Styles: book.css `.chip`, `#chips`, `.chips`.
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { Snapshot } from "@/api";
import { type BoardFilter, chipsOf, EMPTY } from "@/lib/filter";
import { useFlag } from "@/lib/hooks";
import { Icon } from "./icon";
import { LinkButton } from "./link-button";
type ChipProps = {
  label: string;
  sub?: ReactNode;
  t?: string;
  id?: string;
  removeLabel: string;
  onRemove: () => void;
  onOpen?: () => void;
};

/** Without `onOpen` the whole chip removes (filters); with it the label opens and only × removes. */
export function Chip({
  label,
  sub,
  t = "chip",
  id,
  removeLabel,
  onRemove,
  onOpen
}: ChipProps) {
  const x = <span className="x" aria-hidden>
			{"\u00d7"}
		</span>;
  if (!onOpen) return <button type="button" className="chip" data-t={t} data-key={id} aria-label={`${removeLabel} ${label}`} onClick={onRemove}>
				<span>{label}</span>
				{x}
			</button>;
  return <span className="chip split" data-t={t} data-id={id}>
			<button type="button" className="open" onClick={onOpen}>
				<span>{label}</span>
				{sub && <span className="sub">{sub}</span>}
			</button>
			<button type="button" className="remove" data-t={`${t}-remove`} aria-label={`${removeLabel} ${label}`} onClick={onRemove}>
				{x}
			</button>
		</span>;
}
type Props = {
  filter: BoardFilter;
  snapshot: Snapshot | null;
  setFilter: (f: BoardFilter | ((f: BoardFilter) => BoardFilter)) => void;
};

/** The board's floating pill, always there on the board: Filter (opens the filter sheet, ?filter=1), then a chip per
 *  active filter and Clear. */
export function Chips(t0) {
  const $ = _c(39);
  const {
    filter,
    snapshot,
    setFilter
  } = t0;
  const {
    t
  } = useTranslation();
  const sheet = useFlag("filter");
  let chips;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[0] !== filter || $[1] !== setFilter || $[2] !== sheet.on || $[3] !== sheet.toggle || $[4] !== snapshot || $[5] !== t) {
    chips = chipsOf(filter, snapshot, {
      me: t("filter.me"),
      none: t("filter.unassigned")
    });
    t1 = "chips";
    t2 = "chips";
    t3 = "toolbar";
    if ($[13] !== t) {
      t4 = t("filter.active");
      $[13] = t;
      $[14] = t4;
    } else {
      t4 = $[14];
    }
    const t7 = sheet.on;
    const t8 = sheet.toggle;
    let t9;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <Icon name="filter" size={14} />;
      $[15] = t9;
    } else {
      t9 = $[15];
    }
    let t10;
    if ($[16] !== t) {
      t10 = t("filter.title");
      $[16] = t;
      $[17] = t10;
    } else {
      t10 = $[17];
    }
    let t11;
    if ($[18] !== t10) {
      t11 = <span>{t10}</span>;
      $[18] = t10;
      $[19] = t11;
    } else {
      t11 = $[19];
    }
    if ($[20] !== sheet.on || $[21] !== sheet.toggle || $[22] !== t11) {
      t5 = <button type="button" className="chip filter-open" data-t="filter-open" aria-expanded={t7} onClick={t8}>{t9}{t11}</button>;
      $[20] = sheet.on;
      $[21] = sheet.toggle;
      $[22] = t11;
      $[23] = t5;
    } else {
      t5 = $[23];
    }
    let t12;
    if ($[24] !== setFilter || $[25] !== t) {
      t12 = c => <Chip key={c.key} id={c.key} label={c.label} removeLabel={t("filter.remove")} onRemove={() => setFilter(c.remove)} />;
      $[24] = setFilter;
      $[25] = t;
      $[26] = t12;
    } else {
      t12 = $[26];
    }
    t6 = chips.map(t12);
    $[0] = filter;
    $[1] = setFilter;
    $[2] = sheet.on;
    $[3] = sheet.toggle;
    $[4] = snapshot;
    $[5] = t;
    $[6] = chips;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    chips = $[6];
    t1 = $[7];
    t2 = $[8];
    t3 = $[9];
    t4 = $[10];
    t5 = $[11];
    t6 = $[12];
  }
  let t7;
  if ($[27] !== chips.length || $[28] !== setFilter || $[29] !== t) {
    t7 = chips.length > 1 && <LinkButton className="chips-clear" data-t="chips-clear" onClick={() => setFilter(_temp)}>{t("filter.clear")}</LinkButton>;
    $[27] = chips.length;
    $[28] = setFilter;
    $[29] = t;
    $[30] = t7;
  } else {
    t7 = $[30];
  }
  let t8;
  if ($[31] !== t1 || $[32] !== t2 || $[33] !== t3 || $[34] !== t4 || $[35] !== t5 || $[36] !== t6 || $[37] !== t7) {
    t8 = <div id={t1} data-t={t2} role={t3} aria-label={t4}>{t5}{t6}{t7}</div>;
    $[31] = t1;
    $[32] = t2;
    $[33] = t3;
    $[34] = t4;
    $[35] = t5;
    $[36] = t6;
    $[37] = t7;
    $[38] = t8;
  } else {
    t8 = $[38];
  }
  return t8;
}
function _temp(f) {
  return {
    ...EMPTY,
    project: f.project
  };
}